import { FeatureFlagName, FeatureFlags } from '@repo/shared/dtos/feature-flag'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@repo/ui/dropdownMenu'
import { Switch } from '@repo/ui/switch'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { Cfl } from '@repo/ui/utils'
import { ColumnDef } from '@tanstack/react-table'
import { CameraIcon, Edit, EllipsisVertical, ExternalLinkIcon, Users } from 'lucide-react'
import { Link } from 'react-router-dom'

import { DataTableColumnHeader } from '@/components/admin/members/ColHeader'
import { OrganizationAccessManager } from '@/components/sudo/organization/OrganizationAccessManager'
import { OrganizationTerminator } from '@/components/sudo/organization/OrganizationTerminator'
import {
  buildRoute,
  buildSearchParam,
  RouteNames,
  SearchParamsKeyNames,
  SearchParamsViewNames,
} from '@/routes'
import { SudoContextOrganization } from '@/store/sudo'

type ColumnsProps = {
  myOrganizationIds: string[]
  edit: (organization: SudoContextOrganization | null) => void
  takeSnapshot: (organization: SudoContextOrganization | null) => void
  toggleOrganization: (params: {
    featureFlagKey: FeatureFlagName
    organizationId: string
    enabled: boolean
  }) => Promise<void>
}

export const organizationsColumns = ({
  myOrganizationIds,
  edit,
  takeSnapshot,
  toggleOrganization,
}: ColumnsProps): ColumnDef<SudoContextOrganization>[] => {
  // Create dynamic feature flag columns
  const featureFlagColumns: ColumnDef<SudoContextOrganization>[] = FeatureFlags.map((flag) => ({
    id: `featureFlag_${flag.key}`,
    accessorFn: (row) => Array.isArray(row.featureFlags) && row.featureFlags.includes(flag.key),
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          title={Cfl(flag.key)}
          column={column}
          options={{ sortable: true, searchable: false }}
        />
      )
    },
    cell: ({ row }) => {
      const hasFlag =
        Array.isArray(row.original.featureFlags) && row.original.featureFlags.includes(flag.key)
      return (
        <Switch
          checked={hasFlag}
          onCheckedChange={(checked) => {
            void toggleOrganization({
              featureFlagKey: flag.key,
              organizationId: row.original.id,
              enabled: checked,
            })
          }}
        />
      )
    },
  }))

  return [
    {
      id: 'actions',
      cell: ({ row }) => {
        const organization = row.original
        const isMember = myOrganizationIds.some((id) => id === organization.id)
        return (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="hover:text-primary-600 text-primary-300 data-[state=open]:text-primary-600 cursor-pointer px-2">
                  <EllipsisVertical className="size-5" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="bottom" align="start" className="w-fit">
                <DropdownMenuLabel className="select-none">
                  <div className="text-primary-600 flex truncate">{organization.name}</div>
                </DropdownMenuLabel>

                <DropdownMenuSeparator />

                <DropdownMenuGroup>
                  {isMember && (
                    <>
                      <DropdownMenuItem>
                        <Link
                          target="_blank"
                          to={buildRoute(RouteNames.Organizations, [
                            organization.id,
                            RouteNames.Admin +
                              buildSearchParam(
                                SearchParamsKeyNames.View,
                                SearchParamsViewNames.MEMBERS,
                              ),
                          ])}
                          className="flex items-center gap-2"
                        >
                          <Users className="size-4" />
                          <span>See Members</span>
                        </Link>
                      </DropdownMenuItem>

                      <DropdownMenuSeparator />

                      <DropdownMenuItem onClick={() => takeSnapshot(organization)}>
                        <CameraIcon className="size-4" />
                        Take Snapshot
                      </DropdownMenuItem>

                      <DropdownMenuSeparator />

                      <OrganizationTerminator organizationId={organization.id} />

                      <DropdownMenuSeparator />
                    </>
                  )}

                  <OrganizationAccessManager
                    organizationId={organization.id}
                    myOrganizationIds={myOrganizationIds}
                    isMember={isMember}
                  />

                  <DropdownMenuSeparator />

                  <DropdownMenuItem onClick={() => edit(organization)}>
                    <Edit className="size-4" />
                    <span>Edit</span>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )
      },
    },
    {
      accessorKey: 'name',
      header: ({ column }) => {
        return (
          <DataTableColumnHeader column={column} options={{ sortable: true, searchable: true }} />
        )
      },
      cell: ({ row, column }) => {
        const value = row.getValue<string>(column.id)
        const organizationId = row.original.id
        const isMember = myOrganizationIds.some((id) => id === organizationId)
        return (
          <>
            {isMember ? (
              <TooltipWrapper content={`Open ${value}`}>
                <Link
                  target="_blank"
                  to={buildRoute(RouteNames.Organizations, [organizationId])}
                  className="flex items-center gap-2 text-green-600 hover:underline"
                >
                  {value}
                  <ExternalLinkIcon className="size-4" />
                </Link>
              </TooltipWrapper>
            ) : (
              <div>{value}</div>
            )}
          </>
        )
      },
    },
    {
      accessorKey: 'url',
      header: ({ column }) => {
        return (
          <DataTableColumnHeader column={column} options={{ sortable: true, searchable: true }} />
        )
      },
    },
    ...featureFlagColumns,
  ]
}
